import React, { useState, useMemo, useEffect } from 'react';
import { RefreshCw, Loader, Filter, ChevronDown as ChevronDownIcon, ChevronRight, Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useProjects } from '../../contexts/ProjectsContext';
import { useReports } from '../../contexts/ReportsContext';
import { TimesheetForm } from './TimesheetForm';
import { TimesheetMobileCard } from './TimesheetMobileCard';
import { getMonthName } from '../../utils/dateHelpers';

export const TimesheetView = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { projects, loadingProjects, refreshProjects } = useProjects();
  const {
    submittedReports,
    timesheetFilters, setTimesheetFilters,
    expandedTimesheetMonths,
    toggleTimesheetMonth,
    filterTimesheetReports,
    groupReportsByMonth,
    calculateMonthTimesheetTotal,
  } = useReports();

  const [showForm, setShowForm] = useState(false);

  const userReports = useMemo(() => {
    if (!currentUser) return [];
    return submittedReports.filter(r => {
      if (r.type !== 'timesheet') return false;
      if (r.user_id && currentUser.id && r.user_id === currentUser.id) return true;
      if (r.userName && currentUser.name && r.userName === currentUser.name) return true;
      if (r.username && currentUser.username && r.username === currentUser.username) return true;
      return false;
    });
  }, [submittedReports, currentUser]);

  useEffect(() => {
    console.log(`TimeSheet: отчетов для пользователя: ${userReports.length}`);
  }, [userReports.length]);

  const filteredReports = filterTimesheetReports(userReports);
  const groupedByMonth = groupReportsByMonth(filteredReports);

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-md overflow-hidden mb-6`}>
      {/* Заголовок */}
      <div className={`px-4 md:px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h2 className={`text-lg md:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          TimeSheet Отчеты
        </h2>
        <p className={`text-xs md:text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Учет рабочего времени по проектам
        </p>
      </div>

      {/* Основной контент */}
      <div className="p-4 md:p-6 space-y-4">
        {/* Кнопка добавления и обновление проектов */}
        <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-3 mb-4">
          <h3 className="font-bold text-base md:text-lg">Список проектов</h3>
          <button
            onClick={refreshProjects}
            disabled={loadingProjects}
            className={`flex items-center justify-center space-x-1 px-3 py-2 rounded-lg transition-colors touch-target w-full xs:w-auto ${
              darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-50'
            }`}
          >
            {loadingProjects ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="text-sm">Обновить</span>
          </button>
        </div>

        {loadingProjects ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader className="w-8 h-8 animate-spin text-blue-500 mb-2" />
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Загрузка списка проектов...
            </p>
          </div>
        ) : (
          <button
            onClick={() => setShowForm(true)}
            className="w-full touch-target bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Добавить отчеты о рабочем времени</span>
          </button>
        )}

        {/* Фильтры */}
        <div className={`p-3 md:p-4 rounded-lg ${
          darkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-50 border border-gray-200'
        }`}>
          <h4 className="font-medium mb-3 flex items-center text-sm md:text-base">
            <Filter className="w-4 h-4 mr-2" />
            Фильтры
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Проект
              </label>
              <select
                value={timesheetFilters.project}
                onChange={(e) => setTimesheetFilters({...timesheetFilters, project: e.target.value})}
                className={`w-full px-3 py-2 md:py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                }`}
              >
                <option value="">Все проекты</option>
                {projects.map(project => (
                  <option key={project} value={project}>{project}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Месяц
              </label>
              <select
                value={timesheetFilters.month}
                onChange={(e) => setTimesheetFilters({...timesheetFilters, month: e.target.value})}
                className={`w-full px-3 py-2 md:py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                }`}
              >
                <option value="">Все месяцы</option>
                {[...new Set(userReports.map(r => getMonthName(r.submittedAt)))]
                  .sort()
                  .reverse()
                  .map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        {/* Список отчетов */}
        {userReports.length > 0 ? (
          <div className="mt-6">
            <h3 className={`font-bold text-base md:text-lg mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Ваши отчеты
            </h3>
            {Object.entries(groupedByMonth)
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([month, reports]) => (
                <div key={month} className={`mb-4 border rounded-lg ${
                  darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                }`}>
                  <button
                    onClick={() => toggleTimesheetMonth(month)}
                    className={`w-full px-4 py-3 md:py-4 text-left flex justify-between items-center font-bold ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm md:text-base">{month}</span>
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {reports.length} отч.
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        calculateMonthTimesheetTotal(reports) > 100
                          ? (darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800')
                          : (darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800')
                      }`}>
                        {calculateMonthTimesheetTotal(reports)}%
                      </span>
                      {expandedTimesheetMonths?.includes(month) ? (
                        <ChevronDownIcon className="w-4 h-4 md:w-5 md:h-5" />
                      ) : (
                        <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                      )}
                    </div>
                  </button>
                  {expandedTimesheetMonths?.includes(month) && (
                    <div className="p-3 md:p-4 border-t border-gray-200 dark:border-gray-700">
                      {/* Десктопная таблица */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <tr>
                              <th className="p-2 text-left">Проект</th>
                              <th className="p-2 text-left">Дни</th>
                              <th className="p-2 text-left">Процент</th>
                              <th className="p-2 text-left">Дата</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map(report => (
                              <tr key={report.id} className="border-t border-gray-200 dark:border-gray-700">
                                <td className="p-2 max-w-xs truncate">{report.project}</td>
                                <td className="p-2">{report.days} дн.</td>
                                <td className={`p-2 font-bold ${
                                  parseFloat(report.percentage) > 25
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-green-600 dark:text-green-400'
                                }`}>
                                  {report.percentage}%
                                </td>
                                <td className="p-2">{new Date(report.submittedAt).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Мобильные карточки */}
                      <div className="md:hidden space-y-2">
                        {reports.map(report => (
                          <TimesheetMobileCard key={report.id} report={report} darkMode={darkMode} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center py-8 md:py-12">
            <p className={`text-base md:text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              У вас пока нет отправленных отчетов TimeSheet
            </p>
          </div>
        )}
      </div>

      {showForm && <TimesheetForm onClose={() => setShowForm(false)} />}
    </div>
  );
};

export default TimesheetView;